import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { factorService, scenarioService, narrativeService } from '../services/api';

const CaseOverview = ({ case_, workspace }) => {
  const { t } = useTranslation();
  const [factors, setFactors] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [narratives, setNarratives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    fetchOverviewData();
  }, [case_.id]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!showSlideshow) return;
      
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        handleNextSlide();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevSlide();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleCloseSlideshow();
      }
    };

    if (showSlideshow) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [showSlideshow, currentSlide, scenarios]);

  const fetchOverviewData = async () => {
    try {
      const [factorsData, driversData, scenariosData] = await Promise.all([
        factorService.getCaseFactors(case_.id),
        factorService.getCaseDrivers(case_.id),
        scenarioService.getCaseScenarios(case_.id)
      ]);
      
      setFactors(factorsData);
      setDrivers(driversData);
      setScenarios(scenariosData);
      
      // Fetch narratives for selected scenarios
      const selectedScenarios = scenariosData.filter(s => s.is_selected);
      if (selectedScenarios.length > 0) {
        const allNarratives = await Promise.all(
          selectedScenarios.map(scenario => 
            narrativeService.getScenarioNarratives(scenario.id)
          )
        );
        setNarratives(allNarratives.flat());
      }
    } catch (error) {
      console.error('Error fetching overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-neutral-500">
        {t('caseOverview.loading')}
      </div>
    );
  }

  const selectedScenarios = scenarios.filter(s => s.is_selected);
  const progressStats = {
    factors: factors.length,
    drivers: drivers.length,
    scenarios: scenarios.length,
    selectedScenarios: selectedScenarios.length,
    narratives: narratives.length
  };

  // Aquí hi havia `getFrameworkCategories`, `categories` i `factorsByCategory`,
  // que servien per pintar tots els factors agrupats per categoria. Al resum
  // això no hi pinta res: el que importa és què **defineix** els escenaris, i
  // els factors que van quedar fora del test de drivers no en defineixen cap.
  // La classificació sencera és a la passa 1, que és on toca.

  // Els factors secundaris. Al resum només hi surten els que tenen un paper
  // definit: qui no és driver i té assignació.
  const secundaris = factors.filter(f => !f.is_driver && f.secondary_factor_assignment);

  const getTranslatedCategory = (category) => {
    const categoryMap = {
      'political': t('factors.pestel.political'),
      'economic': t('factors.pestel.economic'),
      'social': t('factors.pestel.social'),
      'technological': t('factors.pestel.technological'),
      'environmental': t('factors.pestel.environmental'),
      'legal': t('factors.pestel.legal'),
      'strengths': t('factors.swot.strengths'),
      'weaknesses': t('factors.swot.weaknesses'),
      'opportunities': t('factors.swot.opportunities'),
      'threats': t('factors.swot.threats'),
      'identify': t('factors.cyber.identify'),
      'protect': t('factors.cyber.protect'),
      'detect': t('factors.cyber.detect'),
      'respond': t('factors.cyber.respond'),
      'recover': t('factors.cyber.recover')
    };
    
    return categoryMap[category] || category;
  };

  const handleStartSlideshow = () => {
    const selected = scenarios.filter(s => s.is_selected);
    if (selected.length > 0) {
      setCurrentSlide(0);
      setShowSlideshow(true);
    }
  };

  const handleCloseSlideshow = () => {
    setShowSlideshow(false);
    setCurrentSlide(0);
  };

  const handleNextSlide = () => {
    const selected = scenarios.filter(s => s.is_selected);
    setCurrentSlide((prev) => 
      prev < selected.length - 1 ? prev + 1 : 0
    );
  };

  const handlePrevSlide = () => {
    const selected = scenarios.filter(s => s.is_selected);
    setCurrentSlide((prev) => 
      prev > 0 ? prev - 1 : selected.length - 1
    );
  };

  return (
    <div className="space-y-8">
      {/* Case Summary Header */}
      <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
        <div className="px-6 py-6 border-b border-neutral-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-medium text-neutral-900">{case_.name}</h1>
              <p className="text-neutral-600 mt-1">{t('caseOverview.strategicForesight')}</p>
            </div>
            <div className="flex items-center gap-4">
              {case_.time_horizon && (
                <div className="flex items-center gap-1 text-sm text-neutral-600 bg-neutral-50 px-3 py-1 rounded">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {case_.time_horizon}
                </div>
              )}
              <div className="text-sm text-neutral-600 bg-neutral-50 px-3 py-1 rounded">
                {case_.category_system} {t('caseOverview.framework')}
              </div>
              {selectedScenarios.length > 0 && (
                <button
                  onClick={handleStartSlideshow}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-lg hover:bg-accent-700 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-9 0a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2M9 12l2 2 4-4" />
                  </svg>
                  {t('caseOverview.presentScenarios')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Aquí hi havia quatre targetes de colors amb els recomptes de
            drivers, escenaris, escenaris triats i narratives. S'han tret el
            09/08/2026: els números ja surten a cada secció d'aquesta mateixa
            pàgina i a l'assistent, i quatre rectangles blau, lila, verd i
            taronja eren el que feia que la pantalla semblés un joc. */}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          El resum, en fitxes (redisseny de l'11/08/2026, opció A)

          Abans això barrejava el que serveix per **consultar** amb el que
          serveix per **llegir**, i el resultat era que ni una cosa ni l'altra:
          els drivers en una secció, els escenaris en una altra amb els seus
          valors repetits, i les narratives retallades a tres línies amb un
          «llegeix-ne més» que no portava enlloc. També hi sortien els factors
          que **no** van qualificar com a driver, que al resum no hi pinten res:
          el que importa és què **defineix** els escenaris.

          Ara hi ha dues coses i en aquest ordre:

          1. **La referència**, un sol cop: els drivers amb els seus dos
             extrems, i els factors secundaris dient **a quin driver segueixen**
             —que és el que els fa útils i abans no es veia enlloc.
          2. **Una fitxa per escenari triat**: què el defineix i la seva
             història, seguides. Cada fitxa és una unitat que se'n pot endur qui
             la presenti.
          ══════════════════════════════════════════════════════════════════ */}

      {drivers.length > 0 && (
        <div className="rounded-lg border border-neutral-200 bg-white p-5">
          <h3 className="mb-3 font-mono text-[10.5px] font-medium uppercase tracking-[0.16em] text-neutral-400">
            {t('caseOverview.driversReference')}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border-b border-neutral-200 px-3 pb-2 text-left font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-neutral-400">
                    {t('caseOverview.keyDrivers')}
                  </th>
                  <th className="border-b border-neutral-200 px-3 pb-2 text-left font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-neutral-400">
                    <span className="mr-2">A</span>{t('factors.extremeValues.low')}
                  </th>
                  <th className="border-b border-neutral-200 px-3 pb-2 text-left font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-neutral-400">
                    <span className="mr-2">B</span>{t('factors.extremeValues.high')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {drivers.map(driver => (
                  <tr key={driver.id} className="border-b border-neutral-200 last:border-b-0">
                    <td className="px-3 py-2.5 align-top">
                      <span className="block font-medium text-neutral-900">{driver.causal_factor_name}</span>
                      {driver.causal_factor_category && (
                        <span className="mt-0.5 block text-xs text-neutral-400">
                          {getTranslatedCategory(driver.causal_factor_category)}
                        </span>
                      )}
                    </td>
                    <td className="w-1/3 px-3 py-2.5 align-top text-neutral-600">{driver.extreme_value_low}</td>
                    <td className="w-1/3 px-3 py-2.5 align-top text-neutral-600">{driver.extreme_value_high}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {secundaris.length > 0 && (
            <>
              <h3 className="mb-3 mt-7 font-mono text-[10.5px] font-medium uppercase tracking-[0.16em] text-neutral-400">
                {t('caseOverview.secondaryFactors')}
              </h3>
              <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {secundaris.map(f => {
                  const a = f.secondary_factor_assignment;
                  const segueix = a?.factor_type === 'follows_driver';
                  const noms = (a?.driver_ids || [])
                    .map(id => drivers.find(d => d.id === id)?.causal_factor_name)
                    .filter(Boolean);
                  return (
                    <li key={f.id} className="rounded border border-neutral-200 px-3 py-2">
                      <span className="block text-sm font-medium text-neutral-900">{f.name}</span>
                      <span className="mt-0.5 block text-xs text-neutral-500">
                        {segueix && noms.length
                          ? t('caseOverview.followsDrivers', { drivers: noms.join(' · ') })
                          : a?.factor_type === 'constant'
                          ? t('caseOverview.staysConstant')
                          : t('caseOverview.notAssigned')}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      )}

      {/* Una fitxa per escenari triat */}
      {selectedScenarios.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 px-4 py-12 text-center">
          <p className="text-sm text-neutral-600">{t('caseOverview.developNarrativesMessage')}</p>
        </div>
      ) : (
        selectedScenarios.map((scenario, index) => {
          const narrativa = narratives.find(n => n.scenario_id === scenario.id);
          const trams = [
            { clau: 'initialStage', text: narrativa?.initial_stage },
            { clau: 'intermediateStage', text: narrativa?.intermediate_stage },
            { clau: 'finalStage', text: narrativa?.final_stage }
          ].filter(x => (x.text || '').trim());

          return (
            <article key={scenario.id} className="rounded-lg border border-neutral-200 bg-white p-6">
              <header className="flex flex-wrap items-baseline gap-3 border-b border-neutral-200 pb-3">
                <span className="font-mono text-xs font-semibold text-accent-700">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h3 className="text-lg font-semibold tracking-[-0.01em] text-neutral-900">
                  {scenario.name || t('narratives.defaultScenarioName', { id: scenario.id })}
                </h3>
              </header>

              {/* Què el defineix */}
              {scenario.driver_values && Object.keys(scenario.driver_values).length > 0 && (
                <dl className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                  {Object.entries(scenario.driver_values).map(([driverId, value]) => {
                    const driver = drivers.find(d => d.id.toString() === driverId);
                    const esB = value === driver?.extreme_value_high;
                    return (
                      <div key={driverId}>
                        <dt className="font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-neutral-400">
                          {driver?.causal_factor_name}
                        </dt>
                        <dd className="mt-1 flex items-start gap-2 text-sm text-neutral-800">
                          <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-[3px] border border-neutral-300 font-mono text-[9px] font-bold text-neutral-500">
                            {esB ? 'B' : 'A'}
                          </span>
                          <span>{value}</span>
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              )}

              {/* La seva història. Sencera: retallar-la a tres línies era
                  ensenyar-ne el principi i prou. */}
              {trams.length > 0 ? (
                <ol className="mt-6 border-t border-neutral-200 pt-5">
                  {trams.map((tram, i) => (
                    <li key={tram.clau} className="relative mb-5 pl-9 last:mb-0">
                      {i < trams.length - 1 && (
                        <span className="absolute left-[11px] top-7 bottom-[-1.25rem] w-px bg-neutral-200" aria-hidden="true" />
                      )}
                      <span className="absolute left-0 top-0 flex h-[22px] w-[22px] items-center justify-center rounded-full border border-accent-700 font-mono text-[10px] font-semibold text-accent-700">
                        {i + 1}
                      </span>
                      <h4 className="mt-0.5 font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-neutral-400">
                        {t(`narratives.${tram.clau}`)}
                      </h4>
                      <p className="mt-1.5 max-w-[68ch] text-sm leading-[1.7] text-neutral-700">
                        {tram.text}
                      </p>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="mt-6 border-t border-neutral-200 pt-5 text-sm text-neutral-500">
                  {t('caseOverview.narrativeNeeded')}
                </p>
              )}
            </article>
          );
        })
      )}

      {/* ------------------------------------------------------------------
          Presentació dels escenaris
          
          Això es projecta en una aula i algú hi parla a sobre. Tot el disseny
          en surt d'aquí:
          
          - **Pantalla sencera, sense targeta.** Abans era una targeta blanca
            amb ombra dins d'un fons negre: es perdia espai i semblava un
            diàleg, no una diapositiva.
          - **Cos de text gran.** Les narratives són un paràgraf llarg per
            tram; anaven a 14 px i des del fons de l'aula no es llegien.
          - **Una columna, no tres.** Tres paràgrafs llargs en columnes
            estretes no es poden llegir. Ara van l'un sota l'altre amb l'amplada
            justa perquè l'ull no es perdi (uns 62 caràcters).
          - **Sense colorines.** Hi havia capçalera lila i targetes verda, blava
            i lila. Ara tot és tinta sobre paper i un sol accent, el mateix de
            l'aplicació, que només marca el número de l'escenari i els trams.
          - **Els tres trams són una seqüència**, i per això van numerats i units
            per una línia vertical: el dibuix diu el que diu el mètode.
         ------------------------------------------------------------------ */}
      {showSlideshow && selectedScenarios.length > 0 && (
        <div className="fixed inset-0 z-50 bg-neutral-50 overflow-y-auto">
          {(() => {
            const scenario = selectedScenarios[currentSlide];
            const scenarioNarrative = narratives.find(n => n.scenario_id === scenario.id);
            const trams = [
              { clau: 'initialStage', text: scenarioNarrative?.initial_stage },
              { clau: 'intermediateStage', text: scenarioNarrative?.intermediate_stage },
              { clau: 'finalStage', text: scenarioNarrative?.final_stage }
            ].filter(x => x.text);

            return (
              <div className="mx-auto max-w-5xl px-8 pt-10 pb-28 sm:px-12">

                {/* Capçalera discreta: on som, no què mirar */}
                <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-neutral-200 pb-4">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
                    {case_.name}
                    {case_.time_horizon && <span className="text-neutral-400"> · {case_.time_horizon}</span>}
                  </div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-400 tabular-nums">
                    {currentSlide + 1} / {selectedScenarios.length}
                  </div>
                </div>

                {/* El nom de l'escenari, que és el que la gent ha de recordar */}
                <div className="mt-10 flex items-start gap-5">
                  <span className="mt-2 text-[13px] font-bold tabular-nums text-accent-700">
                    {String(currentSlide + 1).padStart(2, '0')}
                  </span>
                  <h2 className="text-[clamp(2rem,4.4vw,3.25rem)] font-semibold leading-[1.08] tracking-[-0.02em] text-neutral-900">
                    {scenario.name || t('narratives.defaultScenarioName', { id: scenario.id })}
                  </h2>
                </div>

                {/* Els drivers, que són el que defineix l'escenari.
                    La fletxa diu si és el valor alt o el baix; abans ho deia un
                    punt vermell o verd, que a més de fer nosa no es distingeix
                    si tens daltonisme. */}
                {scenario.driver_values && Object.keys(scenario.driver_values).length > 0 && (
                  <dl className="mt-10 grid gap-x-10 gap-y-5 border-t border-neutral-200 pt-6 sm:grid-cols-2">
                    {Object.entries(scenario.driver_values).map(([driverId, value]) => {
                      const driver = drivers.find(d => d.id.toString() === driverId);
                      const esAlt = value === driver?.extreme_value_high;
                      return (
                        <div key={driverId}>
                          <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-neutral-500">
                            {driver?.causal_factor_name}
                          </dt>
                          <dd className="mt-1 flex items-baseline gap-2 text-[clamp(1rem,1.4vw,1.2rem)] text-neutral-900">
                            <span className="font-mono text-sm font-bold text-neutral-400" aria-hidden="true">{esAlt ? 'B' : 'A'}</span>
                            <span>{value}</span>
                          </dd>
                        </div>
                      );
                    })}
                  </dl>
                )}

                {/* Les narratives */}
                {trams.length > 0 ? (
                  <div className="mt-14 border-t border-neutral-200 pt-10">
                    <ol className="space-y-12">
                      {trams.map((tram, i) => (
                        <li key={tram.clau} className="relative pl-12">
                          {/* La línia que uneix els trams: el mètode és una
                              progressió en el temps i el dibuix ho diu. */}
                          {i < trams.length - 1 && (
                            <span className="absolute left-[13px] top-9 bottom-[-3rem] w-px bg-neutral-200" aria-hidden="true" />
                          )}
                          <span className="absolute left-0 top-0 flex h-7 w-7 items-center justify-center rounded-full border border-accent-700 text-[11px] font-bold tabular-nums text-accent-700">
                            {i + 1}
                          </span>
                          <h3 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
                            {t(`narratives.${tram.clau}`)}
                          </h3>
                          <p className="mt-3 max-w-[62ch] text-[clamp(1.05rem,1.5vw,1.3rem)] leading-[1.65] text-neutral-800">
                            {tram.text}
                          </p>
                        </li>
                      ))}
                    </ol>
                  </div>
                ) : (
                  <p className="mt-14 border-t border-neutral-200 pt-10 text-[clamp(1rem,1.4vw,1.2rem)] text-neutral-500">
                    {t('caseOverview.narrativeNeeded')}
                  </p>
                )}
              </div>
            );
          })()}

          {/* Controls. Discrets a posta: durant la projecció no han de competir
              amb el contingut, però han de ser trobables. */}
          <div className="fixed inset-x-0 bottom-0 border-t border-neutral-200 bg-neutral-50/95 backdrop-blur">
            <div className="mx-auto flex max-w-5xl items-center justify-between px-8 py-3 sm:px-12">
              <span className="hidden text-[11px] uppercase tracking-[0.12em] text-neutral-400 sm:block">
                ← → {t('caseOverview.navigate')} · ESC {t('caseOverview.close')}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrevSlide}
                  disabled={currentSlide === 0}
                  className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 transition-colors hover:bg-white disabled:opacity-40"
                >
                  ←
                </button>
                <span className="min-w-[3.5rem] text-center text-sm tabular-nums text-neutral-500">
                  {currentSlide + 1} / {selectedScenarios.length}
                </span>
                <button
                  onClick={handleNextSlide}
                  disabled={currentSlide === selectedScenarios.length - 1}
                  className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 transition-colors hover:bg-white disabled:opacity-40"
                >
                  →
                </button>
                <button
                  onClick={handleCloseSlideshow}
                  className="ml-3 rounded-md bg-neutral-900 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-neutral-700"
                >
                  {t('caseOverview.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CaseOverview;